import { Contract, PaymentFrequency } from '@shared/definitions/contract';
import { PlannedPayment } from '@shared/definitions/payment-planned';

/**
 * Months between successive payments for a given frequency.
 * Defaults to monthly when an unrecognised value is passed in (e.g. legacy data).
 */
export function getMonthsForFrequency(frequency: string | PaymentFrequency): number {
  switch (frequency) {
    case 'monthly': return 1;
    case 'quarterly': return 3;
    case 'annually': return 12;
    default: return 1;
  }
}

/**
 * Adds whole months to a Date without mutating the input.
 *
 * Note: native `setMonth` rolls over when the target month has fewer days
 * (e.g. Jan 31 + 1 month → Mar 3). Callers that need clamping must do so themselves.
 */
export function addMonthsToDate(date: Date, months: number): Date {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

/**
 * Dedupe planned payments by `(paymentUID, date)`. Stable: keeps the first occurrence.
 */
export function removeDuplicatePayments(payments: PlannedPayment[]): PlannedPayment[] {
  const seen = new Set<string>();
  return payments.filter(payment => {
    const key = `${payment.paymentUID}-${payment.date.getTime()}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/**
 * Recursively sanitize an arbitrary value into a Firestore-safe shape:
 * - `null`/`undefined` → `null` (Firestore rejects undefined inside maps)
 * - `Date` instances pass through as-is (Firestore accepts them natively)
 * - arrays are deep-mapped
 * - objects/class-instances are converted to plain objects, dropping function members
 *   and undefined properties
 * - primitives (string/number/boolean) pass through as-is
 *
 * The recursion is the cure for the "Unsupported field value: a custom T object"
 * error you hit when storing class instances or objects with method members.
 */
export function deepSanitizeValueForFirestore(value: unknown): unknown {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(item => deepSanitizeValueForFirestore(item));
  }

  if (typeof value === 'object') {
    const plainObject: Record<string, unknown> = {};
    const allKeys = Object.keys(value as Record<string, unknown>);

    for (const key of allKeys) {
      const propValue = (value as Record<string, unknown>)[key];

      if (propValue === undefined) {
        continue;
      }
      if (typeof propValue === 'function') {
        continue;
      }

      plainObject[key] = deepSanitizeValueForFirestore(propValue);
    }

    return plainObject;
  }

  return value;
}

/**
 * Sanitize a Contract (or any object root) for Firestore storage.
 * Top-level wrapper that always returns a record map.
 */
export function sanitizeContractForFirestore(contract: Contract): Record<string, unknown> {
  return deepSanitizeValueForFirestore(contract) as Record<string, unknown>;
}

/**
 * Bare-minimum contract data check before persistence: must be an object
 * with at least one identifier (contractUID or propertyUID). Heavier validation
 * is delegated to the contract domain layer.
 */
export function isValidContractData(data: Partial<Contract>): boolean {
  return !!data &&
         typeof data === 'object' &&
         !!(data.contractUID || data.propertyUID);
}
